const STORAGE_PREFIX = 'launchContext:';
const FALLBACK_MAX_CONTEXT_AGE_MS = 1000 * 60 * 10; // 10 minutes

const safeJsonParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('LaunchContextService: unable to parse stored context', error);
    return null;
  }
};

const storageAvailable = () => {
  try {
    const testKey = `${STORAGE_PREFIX}__test__`;
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    console.warn('LaunchContextService: localStorage unavailable', error);
    return false;
  }
};

const buildStorageKey = (launchId) => `${STORAGE_PREFIX}${launchId}`;

class LaunchContextService {
  storeContext = (context, { maxAgeMs = FALLBACK_MAX_CONTEXT_AGE_MS } = {}) => {
    if (!context || !storageAvailable()) {
      return null;
    }

    const launchId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const payload = {
      ...context,
      createdAt: Date.now(),
      launchId
    };

    try {
      window.localStorage.setItem(buildStorageKey(launchId), JSON.stringify(payload));
      this.pruneExpiredContexts(maxAgeMs);
      return launchId;
    } catch (error) {
      console.warn('LaunchContextService: failed to persist launch context', error);
      return null;
    }
  }

  consumeContext = (launchId) => {
    if (!launchId || !storageAvailable()) {
      return null;
    }

    const storageKey = buildStorageKey(launchId);
    try {
      const storedValue = window.localStorage.getItem(storageKey);
      if (!storedValue) {
        return null;
      }

      window.localStorage.removeItem(storageKey);
      return safeJsonParse(storedValue);
    } catch (error) {
      console.warn('LaunchContextService: failed to read launch context', error);
      return null;
    }
  }

  pruneExpiredContexts = (maxAgeMs = FALLBACK_MAX_CONTEXT_AGE_MS) => {
    if (!storageAvailable()) {
      return;
    }

    try {
      const now = Date.now();
      Object.keys(window.localStorage).forEach((storageKey) => {
        if (!storageKey.startsWith(STORAGE_PREFIX)) {
          return;
        }
        const value = window.localStorage.getItem(storageKey);
        if (!value) {
          window.localStorage.removeItem(storageKey);
          return;
        }
        const parsed = safeJsonParse(value);
        if (!parsed || !parsed.createdAt || (now - parsed.createdAt) > maxAgeMs) {
          window.localStorage.removeItem(storageKey);
        }
      });
    } catch (error) {
      console.warn('LaunchContextService: failed to prune contexts', error);
    }
  }
}

export default new LaunchContextService();

